import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Modal,
  Form,
  Select,
  message,
  Avatar,
  Dropdown,
  Tabs,
  DatePicker,
  Row,
  Col,
  Statistic,
  Badge
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  BookOutlined,
  LogoutOutlined,
  HomeOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

export default function StudentAssignments() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [classroomModalVisible, setClassroomModalVisible] = useState(false);
  const [assignType, setAssignType] = useState('training'); // 'training' or 'classroom'
  const [form] = Form.useForm();
  const [classroomForm] = Form.useForm();
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalAssignments: 0,
    activeClassrooms: 0,
    pendingAssignments: 0
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [studentsRes, trainingsRes, classroomsRes, assignmentsRes] = await Promise.allSettled([
        api.get('/users?role=student'),
        api.get('/trainings'),
        api.get('/classrooms'),
        api.get('/assignments')
      ]);

      if (studentsRes.status === 'fulfilled') {
        setStudents(studentsRes.value.data);
        setStats(prev => ({ ...prev, totalStudents: studentsRes.value.data.length }));
      }
      if (trainingsRes.status === 'fulfilled') {
        setTrainings(trainingsRes.value.data.filter(t => t.is_active));
      }
      if (classroomsRes.status === 'fulfilled') {
        setClassrooms(classroomsRes.value.data);
        setStats(prev => ({ ...prev, activeClassrooms: classroomsRes.value.data.filter(c => c.is_active).length }));
      }
      if (assignmentsRes.status === 'fulfilled') {
        setAssignments(assignmentsRes.value.data);
        setStats(prev => ({
          ...prev,
          totalAssignments: assignmentsRes.value.data.length,
          pendingAssignments: assignmentsRes.value.data.filter(a => a.status === 'assigned').length
        }));
      }
    } catch (error) {
      message.error('Failed to load data');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/admin/dashboard'),
    },
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/admin/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const handleAssignTraining = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const assignmentRequests = selectedStudents.map(studentId => ({
        user_id: studentId,
        training_id: values.training_id,
        due_date: values.due_date?.format('YYYY-MM-DD')
      }));

      await api.post('/assignments/bulk', { assignments: assignmentRequests });
      message.success(`Training assigned to ${selectedStudents.length} students`);

      setAssignModalVisible(false);
      setSelectedStudents([]);
      form.resetFields();
      fetchAllData();
    } catch (error) {
      message.error('Failed to assign training');
      console.error('Error assigning training:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToClassroom = async () => {
    try {
      const values = await classroomForm.validateFields();
      setLoading(true);

      await api.post(`/classrooms/${values.classroom_id}/students`, {
        student_ids: selectedStudents
      });

      message.success(`${selectedStudents.length} students added to classroom`);

      setClassroomModalVisible(false);
      setSelectedStudents([]);
      classroomForm.resetFields();
      fetchAllData();
    } catch (error) {
      message.error('Failed to assign students to classroom');
      console.error('Error assigning to classroom:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      setLoading(true);
      await api.delete(`/assignments/${assignmentId}`);
      message.success('Assignment deleted successfully');
      fetchAllData();
    } catch (error) {
      message.error('Failed to delete assignment');
      console.error('Error deleting assignment:', error);
    } finally {
      setLoading(false);
    }
  };

  const studentColumns = [
    {
      title: 'Student',
      key: 'student',
      render: (_, record) => (
        <Space>
          <Avatar style={{ backgroundColor: '#faad14' }} icon={<UserOutlined />} />
          <div>
            <div><Text strong>{`${record.first_name} ${record.last_name}`}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Employee ID',
      dataIndex: 'employee_id',
      key: 'employee_id',
    },
    {
      title: 'Assigned Trainings',
      key: 'trainings',
      render: (_, record) => {
        const userAssignments = assignments.filter(a => a.user_id === record.id);
        return <Badge count={userAssignments.length} showZero />;
      }
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    }
  ];

  const assignmentColumns = [
    {
      title: 'Student',
      key: 'student',
      render: (_, record) => (
        <Space>
          <Avatar size="small" style={{ backgroundColor: '#faad14' }} icon={<UserOutlined />} />
          <Text>{`${record.user?.first_name} ${record.user?.last_name}`}</Text>
        </Space>
      ),
    },
    {
      title: 'Training',
      dataIndex: ['training', 'name'],
      key: 'training',
    },
    {
      title: 'Assigned Date',
      dataIndex: 'created_at',
      key: 'assigned_date',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'No due date',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const config = {
          assigned: { color: 'blue', icon: <ClockCircleOutlined />, text: 'Assigned' },
          in_progress: { color: 'orange', icon: <ClockCircleOutlined />, text: 'In Progress' },
          completed: { color: 'success', icon: <CheckCircleOutlined />, text: 'Completed' }
        };
        const { color, icon, text } = config[status] || config.assigned;
        return <Tag color={color} icon={icon}>{text}</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteAssignment(record.id)}
        >
          Remove
        </Button>
      ),
    }
  ];

  const rowSelection = {
    selectedRowKeys: selectedStudents,
    onChange: (selectedRowKeys) => {
      setSelectedStudents(selectedRowKeys);
    },
  };

  const tabs = [
    {
      key: '1',
      label: (
        <span>
          <TeamOutlined />
          Students
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              icon={<BookOutlined />}
              onClick={() => {
                if (selectedStudents.length === 0) {
                  message.warning('Please select students first');
                  return;
                }
                setAssignType('training');
                setAssignModalVisible(true);
              }}
              disabled={selectedStudents.length === 0}
            >
              Assign Training ({selectedStudents.length})
            </Button>
            <Button
              icon={<UsergroupAddOutlined />}
              onClick={() => {
                if (selectedStudents.length === 0) {
                  message.warning('Please select students first');
                  return;
                }
                setAssignType('classroom');
                setClassroomModalVisible(true);
              }}
              disabled={selectedStudents.length === 0}
            >
              Add to Classroom ({selectedStudents.length})
            </Button>
            {selectedStudents.length > 0 && (
              <Button
                onClick={() => setSelectedStudents([])}
              >
                Clear Selection
              </Button>
            )}
          </div>
          <Table
            rowSelection={rowSelection}
            columns={studentColumns}
            dataSource={students}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} students`,
            }}
          />
        </Card>
      ),
    },
    {
      key: '2',
      label: (
        <span>
          <BookOutlined />
          Assignments
        </span>
      ),
      children: (
        <Card>
          <Table
            columns={assignmentColumns}
            dataSource={assignments}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} assignments`,
            }}
          />
        </Card>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/admin/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Student Assignments</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>Student Assignment Management</Title>
          <Text type="secondary">Assign trainings and manage student classroom enrollment</Text>
        </div>

        {/* Stats Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Students"
                value={stats.totalStudents}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Assignments"
                value={stats.totalAssignments}
                prefix={<BookOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active Classrooms"
                value={stats.activeClassrooms}
                prefix={<UsergroupAddOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending Assignments"
                value={stats.pendingAssignments}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>

        <Tabs items={tabs} defaultActiveKey="1" />
      </Content>

      {/* Assign Training Modal */}
      <Modal
        title="Assign Training to Students"
        open={assignModalVisible}
        onOk={handleAssignTraining}
        onCancel={() => {
          setAssignModalVisible(false);
          form.resetFields();
        }}
        width={600}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item>
            <Text type="secondary">
              Assigning training to {selectedStudents.length} selected student(s)
            </Text>
          </Form.Item>

          <Form.Item
            name="training_id"
            label="Select Training"
            rules={[{ required: true, message: 'Please select a training' }]}
          >
            <Select placeholder="Choose a training to assign">
              {trainings.map(training => (
                <Option key={training.id} value={training.id}>
                  {training.name} ({training.type === 'self_paced' ? 'Self-Paced' : 'Classroom'})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="due_date"
            label="Due Date (Optional)"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign to Classroom Modal */}
      <Modal
        title="Add Students to Classroom"
        open={classroomModalVisible}
        onOk={handleAssignToClassroom}
        onCancel={() => {
          setClassroomModalVisible(false);
          classroomForm.resetFields();
        }}
        width={600}
        confirmLoading={loading}
      >
        <Form form={classroomForm} layout="vertical">
          <Form.Item>
            <Text type="secondary">
              Adding {selectedStudents.length} student(s) to classroom
            </Text>
          </Form.Item>

          <Form.Item
            name="classroom_id"
            label="Select Classroom"
            rules={[{ required: true, message: 'Please select a classroom' }]}
          >
            <Select placeholder="Choose a classroom">
              {classrooms.filter(c => c.is_active).map(classroom => (
                <Option key={classroom.id} value={classroom.id}>
                  {classroom.name} - {classroom.training?.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}